import { ApiClient } from '../apiClient';
import { LinkModel, LinkDestination } from '../types';

export const LinksService = {
  getLinks: async (params?: {
    searchTerm?: string;
    categoryId?: string;
    archived?: boolean;
  }): Promise<LinkModel[]> => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.searchTerm) {
        queryParams.append('searchTerm', params.searchTerm);
      }
      
      if (params?.categoryId) {
        queryParams.append('categoryId', params.categoryId);
      }
      
      if (params?.archived !== undefined) {
        queryParams.append('archived', params.archived.toString());
      }

      const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await ApiClient.get<LinkModel[]>(`/links${query}`);
      
      if (!response.data) {
        throw new Error('Failed to fetch links');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching links:', error);
      throw error;
    }
  },

  getLink: async (id: string): Promise<LinkModel> => {
    try {
      const response = await ApiClient.get<LinkModel>(`/links/${id}`);
      
      if (!response.data) {
        throw new Error(`Failed to fetch link with ID: ${id}`);
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching link with ID ${id}:`, error);
      throw error;
    }
  },

  getLinkDestination: async (id: string): Promise<LinkDestination> => {
    try {
      const response = await ApiClient.get<LinkDestination>(`/links/${id}/destination`);
      
      if (!response.data) {
        throw new Error(`Failed to fetch destination for link with ID: ${id}`);
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching destination for link with ID ${id}:`, error);
      throw error;
    }
  },

  createLink: async (
    link: Partial<LinkModel>,
    destination?: Partial<LinkDestination>
  ): Promise<LinkModel> => {
    try {
      const payload = destination ? { ...link, destination } : link;
      const response = await ApiClient.post<LinkModel>('/links', payload);
      
      if (!response.data) {
        throw new Error('Failed to create link');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error creating link:', error);
      throw error;
    }
  },

  updateLink: async (
    id: string,
    link: Partial<LinkModel>,
    destination?: Partial<LinkDestination>
  ): Promise<LinkModel> => {
    try {
      const payload = destination ? { ...link, destination } : link;
      const response = await ApiClient.put<LinkModel>(`/links/${id}`, payload);
      
      if (!response.data) {
        throw new Error(`Failed to update link with ID: ${id}`);
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error updating link with ID ${id}:`, error);
      throw error;
    }
  },

  deleteLink: async (id: string): Promise<void> => {
    try {
      await ApiClient.delete(`/links/${id}`);
    } catch (error) {
      console.error(`Error deleting link with ID ${id}:`, error);
      throw error;
    }
  },

  uploadFile: async (linkId: string, file: File): Promise<void> => {
    try {
      const uploadUrlResponse = await ApiClient.post<{ uploadUrl: string, httpMethod: string, uploadAuthorizationHeader?: string }>(`/linkassets/${linkId}/files`, {
        fileName: file.name,
        contentType: file.type
      });
      
      if (!uploadUrlResponse.data || !uploadUrlResponse.data.uploadUrl) {
        throw new Error('Failed to get upload URL');
      }
      
      const { uploadUrl, httpMethod, uploadAuthorizationHeader } = uploadUrlResponse.data;
      
      const headers: Record<string, string> = {
        'Content-Type': file.type,
      };
      
      if (uploadAuthorizationHeader) {
        headers['Authorization'] = uploadAuthorizationHeader;
      }

      const uploadResponse = await fetch(uploadUrl, {
        method: httpMethod,
        headers,
        body: file,
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }
}; 